import {
  GetPurchaseCostResponse,
  ProductPricesInterface,
  ProductPricingInterface,
  PricingPlanProductPricing as ApiPricingPlanProductPricing,
} from '@vendasta/billing';
import { BillingStrategy, billingStrategyFromAPI } from './billing-strategy';
import { Commitment, commitmentFromAPI } from './commitment';
import { Currency, currencyFromApi } from './currency';
import { Frequency, frequencyFromApi, frequencyFromLegacyApi, frequencyToApi } from './frequency';
import { ProductPricingRules, productPricingRulesFromApi } from './product-pricing-rules';
import { ProductPricingType, productPricingTypeFromApi, productPricingTypeToApi } from './product-pricing-type';
import { Fee, feesFromApi, feesToApi } from './fee';
import { productPricingRulesToApi } from './pricing-plan-product';

export interface PurchaseCost {
  billingFrequency: Frequency;
  total: number;
  subtotal: number;
  currency: Currency;
  strategy: BillingStrategy;
  commitment: Commitment;
  isStartingPrice: boolean;
  discountTotal: number;
  setupFee: number;
}

export interface ProductPricing {
  strategy: BillingStrategy;
  pricingType: ProductPricingType;
  currency: Currency;
  frequency: Frequency;
  pricingRules: ProductPricingRules[];
  commitment: Commitment;
  volumeCommitment: number;
  isStartingPrice: boolean;
  resolvedFrom?: string;
  setupFee: number;
  fees: Fee[];
}

export interface ProductPrices {
  sku: string;
  prices: ProductPricing[];
}

export function purchaseCostFromAPI(pcApi: GetPurchaseCostResponse): PurchaseCost {
  const subtotal = pcApi.subtotalCost || 0;
  const discountTotal = pcApi.discountAmount || 0;
  const total = pcApi.totalCost || 0;
  const setupFee = pcApi.setupFee || 0;

  return {
    subtotal: subtotal,
    discountTotal: discountTotal,
    total: total,
    billingFrequency: frequencyFromLegacyApi(pcApi.billingFrequency),
    currency: currencyFromApi(pcApi.currency),
    strategy: billingStrategyFromAPI(pcApi.strategy),
    commitment: commitmentFromAPI(pcApi.commitment),
    isStartingPrice: pcApi.isStartingPrice,
    setupFee: setupFee,
  };
}

export function productPricingFromApi(pApi: ProductPricingInterface): ProductPricing {
  return {
    strategy: billingStrategyFromAPI(pApi.strategy),
    pricingType: productPricingTypeFromApi(pApi.pricingType),
    currency: currencyFromApi(pApi.currency),
    frequency: frequencyFromApi(pApi.frequency),
    pricingRules: productPricingRulesFromApi(pApi.pricingRules),
    commitment: commitmentFromAPI(pApi.commitment),
    volumeCommitment: pApi.volumeCommitment || 0,
    isStartingPrice: pApi.isStartingPrice || false,
    resolvedFrom: pApi.resolvedFrom,
    setupFee: pApi.setupFee || 0,
    fees: feesFromApi(pApi.fees || []),
  };
}

export function pricesFromApi(pApi: ProductPricesInterface[]): ProductPrices[] {
  return pApi?.map(
    (p) =>
      p &&
      ({
        sku: p.sku,
        prices: (p.prices || []).map((price) => price && productPricingFromApi(price)),
      } as ProductPrices),
  );
}

export function productPricingToApiCurrencyMap(productPricing: ProductPricing): {
  [currencyCode: string]: ApiPricingPlanProductPricing;
} {
  if (!productPricing) {
    return {};
  }
  const apiPricing = productPricingToApi(productPricing);
  return { [apiPricing.currencyCode]: apiPricing };
}

export function productPricingToApi(productPricing: ProductPricing): ApiPricingPlanProductPricing {
  return new ApiPricingPlanProductPricing({
    type: productPricingTypeToApi(productPricing.pricingType),
    rules: productPricingRulesToApi(productPricing.pricingRules),
    setupFee: productPricing.setupFee,
    fees: feesToApi(productPricing.fees),
    frequency: frequencyToApi(productPricing.frequency),
    currencyCode: productPricing.currency,
  });
}
